import type { ModeName, ModeCategory, Stop } from '@commutifi/modes';
import type { CommuteProfileProps, CommuteProfileLegProps, PreferredLegShape } from '@commutifi/models/CommuteProfiles';
import { type CommuteCostsProps, CommuteCostFrequency } from '@commutifi/models/CommuteCostsProps';
import { type RouteLegProps, type RouteProps } from '@commutifi/models/Routes';
import { type ScoreProps } from '@commutifi/models/Scores';
import { type MetricProps } from '@commutifi/models/Metrics';
import { type VehicleProps } from '@commutifi/models/Vehicles';
import { LegType } from '@commutifi/constants/LegType';
import { Overwrite } from 'utility-types';
import { CombinedModes } from '@commutifi-fe/constants';
import type { Location } from './Location';
import type { GeocoderAddressComponent } from './thirdParty/google';
import { Days } from '@commutifi/constants/Days';
export { type CommuteProfileProps, type CommuteProfileLegProps, type CommuteCostsProps, CommuteCostFrequency, LegType, type PreferredLegShape };
export type CommuteCost = CommuteCostsProps;
export type Score = ScoreProps;
export type Metric = MetricProps;
export type CommuteProfileLeg = Overwrite<CommuteProfileLegProps, {
  category?: ModeCategory | CombinedModes | null;
}>;
export type CommuteProfile = Overwrite<CommuteProfileProps, {
  category?: ModeCategory | CombinedModes | null;
  legs: CommuteProfileLeg[];
}>;
export type RouteLeg = Partial<RouteLegProps> & {
  category?: ModeCategory | CombinedModes | null;
  id?: string;
  // When a public-transit route is stored, GoogleTransitDirections' getValueFromEvent flattens the
  // selection into a single leg and nests the originally-selected route here, and relocates the
  // "not listed" marker onto the leg (top-level fields are spread onto legs[0]). Readers of a stored
  // route (re-selection on back-nav, the required validator) must look here, not only at the top.
  transit?: TransitOption;
  isRouteNotListed?: boolean;
};
export type RouteLegWithTransit = RouteLeg & {
  transit?: TransitOption;
};
export type CommuteProfileLegWithTransit = CommuteProfileLeg & {
  transit?: TransitOption;
};
export type Route = Overwrite<RouteProps, {
  legs: RouteLeg[];
  weeklyFrequency?: number | null;
}> & {
  arriveAtOffice?: string | null;
  // Set when the commuter picked "My route is not listed" for a public-transit commute. Without it,
  // that choice serializes identically to the auto-filled placeholder, so it can't be told apart
  // from "no transit route chosen" for required-validation or re-selection on back-navigation.
  isRouteNotListed?: boolean;
};
export type RouteWithTransit = Omit<Route, 'legs'> & {
  legs: RouteLegWithTransit[];
};
export type RouteLegWithMetrics = RouteLeg & {
  metrics: Metric;
  // Legacy, this is an error and doesn't exist ?
  textColor?: string;
};
export type TransitLeg = Omit<RouteLeg, 'peopleCount' | 'order'> & {
  displayColor?: string;
  textColor?: string;
  icon?: string;
  distance?: number;
};
export type TransitOption = Overwrite<Route, {
  legs: TransitLeg[];
}> & {
  durationInSec?: number;
  formattedDuration?: string;
};
export type Frequency = {
  days?: DaysValues[] | null;
  weeklyFrequency: number;
};
export type OptionalFrequency = Frequency | undefined;
export { Days as DaysValueEnum };
export type DaysValues = `${Days}`;
export const MonToFri = [Days.Monday, Days.Tuesday, Days.Wednesday, Days.Thursday, Days.Friday];
export type AppAvailableCategories = `${ModeCategory}` | `${CombinedModes}`;
export type CommuteTransport = {
  category?: ModeCategory | CombinedModes | null;
  mode?: ModeName | null;
  stop?: Stop | null;
};

// This format is based on the answer we get for offices suggest and home address autocomplete.
// To be able to compare the 2 locations we need to define a common format.
export type AddressPoint = {
  addressComponents?: GeocoderAddressComponent[];
  formattedAddress: string;
  location: Location;
};
export type AddressPointWithTimezone = AddressPoint & {
  timezone?: string | null;
};
export type AddressGeocodeValue = AddressPoint & {
  value?: string;
};
export const isAddressGeocodeValue = (val: unknown): val is AddressGeocodeValue => Boolean(val && typeof val === 'object' && 'formattedAddress' in val && 'location' in val);
export type Vehicle = VehicleProps;